import { useState, type ReactNode } from "react";
import { ConfirmModal } from "./ConfirmModal";

/**
 * In-app confirm for a destructive action — the reusable replacement for the browser `confirm()` (+ its
 * failure `alert()`) across the admin UI. `ask({...})` opens the amendie-styled {@link ConfirmModal}; on
 * confirm it runs the action, keeping the dialog open with an inline error if it throws and closing it on
 * success. Render `modal` once wherever the owning component lives.
 *
 *   const del = useConfirmAction();
 *   <button onClick={() => del.ask({ title: "Delete?", danger: true, run: async () => { … } })}>Delete</button>
 *   {del.modal}
 */
export interface ConfirmRequest {
  title: string;
  body?: ReactNode;
  confirmLabel?: string;
  /** Type-to-confirm phrase (e.g. a name) — the confirm button unlocks only when it's typed exactly. */
  confirmPhrase?: string;
  danger?: boolean;
  run: () => Promise<void>;
}

export function useConfirmAction() {
  const [pending, setPending] = useState<ConfirmRequest | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  function ask(cfg: ConfirmRequest): void {
    setErr(null);
    setPending(cfg);
  }

  const modal = pending ? (
    <ConfirmModal
      title={pending.title}
      body={
        <>
          {pending.body}
          {err && (
            <span className="err" style={{ display: "block", marginTop: "var(--sp3)" }}>
              {err}
            </span>
          )}
        </>
      }
      confirmLabel={pending.confirmLabel}
      confirmPhrase={pending.confirmPhrase}
      danger={pending.danger}
      busy={busy}
      onConfirm={() => {
        setBusy(true);
        setErr(null);
        pending
          .run()
          .then(() => setPending(null))
          .catch((e) => setErr(e instanceof Error ? e.message : String(e)))
          .finally(() => setBusy(false));
      }}
      onCancel={() => {
        if (!busy) {
          setPending(null);
          setErr(null);
        }
      }}
    />
  ) : null;

  return { ask, modal };
}
